import { logPurchaseDebug } from '@/utils/purchaseDebug';

/**
 * Purchase Tracker Utility for Google Analytics
 * Handles purchase event tracking with duplicate prevention
 */
class PurchaseTracker {
    constructor() {
        this.processedTransactions = new Set();
        this.isInitialized = false;
        this.init();
    }
    init() {
        if (this.isInitialized) return;

        // Initialize dataLayer if it doesn't exist
        if (typeof window !== 'undefined' && !window.dataLayer) {
            window.dataLayer = [];
        }
        this.isInitialized = true;
    }
    /**
     * Track purchase event with duplicate prevention
     * @param {Object} purchaseData - Purchase data object
     * @param {string} purchaseData.transaction_id - Unique transaction ID
     * @param {number} purchaseData.value - Total transaction value
     * @param {string} purchaseData.currency - Currency code (default: 'UAH')
     * @param {string} purchaseData.phone_number - Customer phone number
     * @param {string} purchaseData.email - Customer email
     * @param {Array} purchaseData.items - Array of purchased items
     */
    trackPurchase(purchaseData) {
        if (!this.isInitialized) {
            console.warn('PurchaseTracker not initialized');
            return;
        }

        // Тимчасово блокуємо view_item під час/після purchase,
        // щоб уникнути дублювання в one-click сценарії.
        if (typeof window !== 'undefined') {
            window.__gaSuppressViewItemUntil = Date.now() + 10000;
        }

        // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ВХОДНЫХ ДАННЫХ
        console.log('🔍 [PURCHASE TRACKER] trackPurchase called with purchaseData:', {
            purchaseData: purchaseData,
            items: purchaseData.items,
            itemsCount: purchaseData.items?.length
        });

        const {
            transaction_id,
            value,
            currency = 'UAH',
            phone_number,
            email,
            items = []
        } = purchaseData;

        // Validate required fields
        if (!transaction_id || !value || !items.length) {
            logPurchaseDebug('TRACKER_SKIP_INVALID_DATA', { transaction_id, value, itemsCount: items.length });
            console.error('Missing required purchase data:', { transaction_id, value, items });
            return;
        }

        // Check for duplicate transaction
        if (this.processedTransactions.has(transaction_id)) {
            logPurchaseDebug('TRACKER_SKIP_DUPLICATE', { transaction_id });
            console.warn('Duplicate transaction detected, skipping:', transaction_id);
            return;
        }

        // Mark transaction as processed
        this.processedTransactions.add(transaction_id);

        try {
            // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ПЕРЕД ФОРМАТИРОВАНИЕМ ITEMS
            console.log('🔍 [PURCHASE TRACKER] Formatting items:', {
                itemsBeforeFormat: items,
                itemsCount: items.length
            });

            // Prepare purchase event data (GA4 ecommerce purchase schema)
            const eventData = {
                event: 'purchase',
                phone_number: this.formatPhoneNumber(phone_number),
                email: email || '',
                ecommerce: {
                    currency: currency || 'UAH',
                    value: parseFloat(value),
                    transaction_id: transaction_id.toString(),
                    items: items.map(item => this.formatItem(item))
                }
            };

            // Exact GA contract: reset ecommerce, then push purchase
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push(eventData);

            logPurchaseDebug('TRACKER_SENT', {
                transaction_id: eventData.ecommerce?.transaction_id,
                value: eventData.ecommerce?.value,
                currency: eventData.ecommerce?.currency,
                itemsCount: eventData.ecommerce?.items?.length ?? 0,
            });

            // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ДЛЯ ОТЛАДКИ
            console.log('===== GOOGLE ANALYTICS PURCHASE EVENT =====');
            console.log('Полные данные, отправленные в dataLayer/GTM:');
            console.log(JSON.stringify(eventData, null, 2));
            console.log('dataLayer length:', window.dataLayer?.length);
            console.log('last dataLayer events:', (window.dataLayer || []).slice(-3));
            console.log('==========================================');

            // Store in localStorage for debugging
            this.storePurchaseData(transaction_id, eventData);
        } catch (error) {
            console.error('Error tracking purchase:', error);
            // Remove from processed set on error to allow retry
            this.processedTransactions.delete(transaction_id);
        }
    }
    /**
     * Format phone number (remove dashes and spaces)
     * @param {string} phone - Phone number
     * @returns {string} Formatted phone number
     */
    formatPhoneNumber(phone) {
        if (!phone) return '';
        return phone.replace(/[\s\-\(\)]/g, '');
    }
    /**
     * Format item data for Google Analytics
     * @param {Object} item - Item data
     * @returns {Object} Formatted item
     */
    formatItem(item) {
        console.log('\n🛍️ [PURCHASE TRACKER] ========== ФОРМАТУВАННЯ ТОВАРУ ==========');
        console.log('🛍️ [PURCHASE TRACKER] Вхідні дані товару:', item);

        // ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ ДЛЯ ОТЛАДКИ ID
        const productId = this.extractProductId(item);
        console.log('🔍 [PURCHASE TRACKER] Витягнутий product_price_id:', productId);
        console.log('🔍 [PURCHASE TRACKER] Тип ID:', typeof productId);

        // ПЕРЕВІРКА: переконуємося, що використовуємо product_price_id, а не product.id
        if (item.id && productId && productId !== item.id) {
            console.log('✅ [PURCHASE TRACKER] ПЕРЕВІРКА: product_price_id (' + productId + ') ≠ item.id (' + item.id + ') - ВСЕ ПРАВИЛЬНО!');
        } else if (productId === item.id) {
            console.warn('⚠️ [PURCHASE TRACKER] УВАГА: product_price_id співпадає з item.id - можливо, використовується неправильний ID!');
        }

        // Извлекаем цену используя логику из checkoutTracker
        const price = this.extractPrice(item);

        // For purchase payload, keep explicit brand from item first.
        // Heuristic extraction from title is only fallback.
        let finalBrand = item.item_brand || item.brand || '';
        if (!finalBrand && (item.item_name || item.title)) {
            const extractedBrand = this.extractBrandFromTitle(item.item_name || item.title);
            if (extractedBrand && extractedBrand.trim()) {
                finalBrand = extractedBrand;
            }
        }
        if (!finalBrand || !finalBrand.trim()) {
            finalBrand = 'Невизначений бренд';
        }
        // For purchase payload, keep explicit category from item first.
        let finalCategory = '';
        if (typeof item.item_category === 'string' && item.item_category.trim()) {
            finalCategory = item.item_category.trim();
        }

        // Fallback to route category only when item category is absent.
        if (!finalCategory) {
            const routeCategory = this.getCurrentCategoryFromRoute();
            if (routeCategory) {
                const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                if (categoryName) {
                    finalCategory = categoryName;
                }
            }
        }

        // Якщо не знайшли в роуті, спробуємо sessionStorage
        if (!finalCategory) {
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    finalCategory = sessionStorage.getItem('ga_last_item_category') || '';
                }
            } catch (e) { /* no-op */ }
        }

        // Якщо все ще не знайшли, спробуємо з даних товару
        if (!finalCategory && item.category) {
            finalCategory = item.category.name || item.category.title || '';
        }

        if (!finalCategory) {
            finalCategory = 'Невизначена категорія';
        }

        const category2 = item.item_category2
            || item.category?.parent?.name
            || '';
        const category3 = item.item_category3
            || item.category?.parent?.parent?.name
            || '';
        const category4 = item.item_category4
            || item.category?.parent?.parent?.parent?.name
            || '';
        const category5 = item.item_category5
            || item.category?.parent?.parent?.parent?.parent?.name
            || '';

        const normalizedId = this.normalizeId(productId);
        console.log('🎯 [PURCHASE TRACKER] ЦЕ БУДЕ item_id В GOOGLE ANALYTICS:', normalizedId, '(це product_price_id!)');

        const formattedItem = {
            item_name: item.item_name || item.title || '',
            item_id: normalizedId, // product_price_id
            price: price,
            currency: item.currency || 'UAH',
            item_brand: finalBrand,
            item_category: finalCategory,
            item_category2: category2,
            item_category3: category3,
            item_category4: category4,
            item_category5: category5,
            quantity: parseInt(item.quantity || 1)
        };

        console.log('✅ [PURCHASE TRACKER] Відформатований товар:');
        console.log('   🆔 item_id:', formattedItem.item_id, '(тип:', typeof formattedItem.item_id, ') - ЦЕ product_price_id!');
        console.log('   📝 Назва:', formattedItem.item_name);
        console.log('   🏷️ Бренд:', formattedItem.item_brand);
        console.log('   📂 Категорія:', formattedItem.item_category);
        console.log('   💰 Ціна:', formattedItem.price, formattedItem.currency);
        console.log('   🔢 Кількість:', formattedItem.quantity);
        console.log('   💵 Загальна вартість:', (formattedItem.price * formattedItem.quantity).toFixed(2), formattedItem.currency);
        console.log('🛍️ [PURCHASE TRACKER] ========== КІНЕЦЬ ФОРМАТУВАННЯ ТОВАРУ ==========\n');

        return formattedItem;
    }
    /**
     * Витягує ціну з даних товару (логика из checkoutTracker)
     * @param {Object} productData - Дані товару
     * @param {Element} element - DOM элемент (опционально)
     * @returns {number}
     */
    extractPrice(productData, element = null) {
        // ПРИОРИТЕТ 1: Ищем цену за единицу в данных товара (из store)
        if (productData.productPrice && productData.productPrice.price && productData.productPrice.price.price) {
            const price = parseFloat(productData.productPrice.price.price);
            if (!isNaN(price) && price > 0) {
                return price;
            }
        }

        // ПРИОРИТЕТ 1.1: Проверяем сохраненную цену за м² (для товаров с расчетом по площади)
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                const savedM2Price = sessionStorage.getItem('ga_m2_price');
                if (savedM2Price) {
                    const price = parseFloat(savedM2Price);
                    if (!isNaN(price) && price > 0) {
                        return price;
                    }
                }
            }
        } catch (e) {
            // Игнорируем ошибки sessionStorage
        }

        // ПРИОРИТЕТ 2: Ищем цену в других местах структуры данных
        if (productData.price) {
            const price = parseFloat(productData.price);
            if (!isNaN(price) && price > 0) {
                return price;
            }
        }

        if (productData.select_type && productData.select_type.price) {
            const price = parseFloat(productData.select_type.price);
            if (!isNaN(price) && price > 0) {
                return price;
            }
        }

        // ПРИОРИТЕТ 3: Если передан конкретный элемент, ищем цену в нем
        if (element) {
            const priceSelectors = [
                '.product__price h5', // ПРИОРИТЕТ 1: цена за единицу товара в h5
                '.product__price', // ПРИОРИТЕТ 2: цена за единицу товара (fallback)
                '.product-price', // ПРИОРИТЕТ 3: цена товара
                '.price', // ПРИОРИТЕТ 4: общий селектор цены
                '[data-price]', // ПРИОРИТЕТ 5: data-атрибут
                '.cost, .amount' // ПРИОРИТЕТ 6: альтернативные селекторы
            ];

            for (const selector of priceSelectors) {
                const priceElement = element.querySelector(selector);
                if (priceElement) {
                    // Сначала проверяем data-атрибуты
                    const dataPrice = priceElement.getAttribute('data-price');
                    if (dataPrice) {
                        const parsedPrice = parseFloat(dataPrice);
                        if (!isNaN(parsedPrice) && parsedPrice > 0) {
                            return parsedPrice;
                        }
                    }
                    // Если data-атрибут не найден, парсим текст
                    const priceText = priceElement.textContent?.trim() || '';
                    if (priceText) {
                        // Парсим цену (убираем пробелы и заменяем запятую на точку)
                        const cleanPrice = priceText.replace(/\s/g, '').replace(',', '.');
                        const parsedPrice = parseFloat(cleanPrice);
                        if (!isNaN(parsedPrice) && parsedPrice > 0) {
                            return parsedPrice;
                        }
                    }
                }
            }
        }

        // Fallback: возвращаем 0 если цену не нашли
        return 0;
    }
    /**
     * Витягує бренд з заголовка товару
     * @param {string} title - Заголовок товару
     * @returns {string}
     */
    extractBrandFromTitle(title) {
        if (!title) {
            return '';
        }
        const parseBrandFromTitle = (title) => {
            const t = (title || '').trim();
            if (!t) return '';
            // Пріоритет 1: Шукаємо відомі бренди з модифікаторами (BauGlas FSX, RODECA, BAUGLAS)
            const knownBrands = ['BauGlas', 'RODECA', 'BAUGLAS'];
            for (const brand of knownBrands) {
                // Спочатку шукаємо бренд з можливими модифікаторами (FSX, Professional, тощо)
                const brandWithModifier = t.match(new RegExp(`\\b${brand}\\s+([A-Z]{2,}(?:\\s+[A-Za-z]+)*)\\b`, 'i'));
                if (brandWithModifier && brandWithModifier[0]) {
                    return brandWithModifier[0];
                }
                // Якщо не знайшли з модифікатором, шукаємо просто бренд
                const brandMatch = t.match(new RegExp(`\\b${brand}\\b`, 'i'));
                if (brandMatch && brandMatch[0]) {
                    return brandMatch[0];
                }
            }
            // Додатковий пошук: шукаємо "BauGlas FSX" як окремий випадок
            const bauGlasFSXMatch = t.match(/\bBauGlas\s+FSX\b/i);
            if (bauGlasFSXMatch && bauGlasFSXMatch[0]) {
                return bauGlasFSXMatch[0];
            }
            // Пріоритет 2: Шукаємо CamelCase (BauGlas, BauGlas FSX)
            const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+(?:\s+[A-Z]+)*\b/);
            if (camelMatch && camelMatch[0]) {
                return camelMatch[0];
            }
            // Пріоритет 3: Шукаємо слова з великих літер (RODECA, BAUGLAS, FSX)
            const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
            if (capsMatch && capsMatch[0]) {
                return capsMatch[0];
            }
            // Пріоритет 4: Спробуємо знайти бренд до коми
            const commaIdx = t.indexOf(',');
            if (commaIdx > 0) {
                const beforeComma = t.slice(0, commaIdx).trim();
                if (beforeComma.length >= 2 && beforeComma.length <= 60) {
                    return beforeComma;
                }
            }
            // Пріоритет 5: Беремо перше слово з 3+ літер
            const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
            if (firstWord && firstWord[0]) {
                return firstWord[0];
            }
            return '';
        };
        return parseBrandFromTitle(title);
    }
    /**
     * Store purchase data in localStorage for debugging
     * @param {string} transactionId - Transaction ID
     * @param {Object} eventData - Event data
     */
    storePurchaseData(transactionId, eventData) {
        try {
            const key = `purchase_${transactionId}`;
            localStorage.setItem(key, JSON.stringify({
                timestamp: new Date().toISOString(),
                data: eventData
            }));
            // Keep only last 10 purchases
            this.cleanupOldPurchases();
        } catch (error) {
            console.warn('Could not store purchase data:', error);
        }
    }
    /**
     * Clean up old purchase data from localStorage
     */
    cleanupOldPurchases() {
        try {
            const keys = Object.keys(localStorage).filter(key => key.startsWith('purchase_'));
            if (keys.length > 10) {
                // Sort by timestamp and remove oldest
                const purchases = keys.map(key => ({
                    key,
                    data: JSON.parse(localStorage.getItem(key))
                })).sort((a, b) => new Date(a.data.timestamp) - new Date(b.data.timestamp));
                // Remove oldest purchases
                const toRemove = purchases.slice(0, keys.length - 10);
                toRemove.forEach(purchase => {
                    localStorage.removeItem(purchase.key);
                });
            }
        } catch (error) {
            console.warn('Could not cleanup old purchases:', error);
        }
    }
    /**
     * Витягує правильний ID товару з пріоритетом product_price_id
     * Використовує ту саму логіку що й basketQuantityTracker.js
     * @param {Object} item - Дані товару
     * @returns {any} ID товару
     */
    extractProductId(item) {
        if (!item) {
            return '';
        }

        // Пріоритет 1: product_price_id на верхньому рівні об'єкта (НАЙВАЖЛИВІШЕ - справжній ID товару з кошика)
        const topLevelProductPriceId = item.product_price_id;
        if (topLevelProductPriceId !== null && topLevelProductPriceId !== undefined && topLevelProductPriceId !== '') {
            return topLevelProductPriceId;
        }

        // Пріоритет 2: productPriceId (camelCase) на верхньому рівні об'єкта
        const topLevelProductPriceIdCamel = item.productPriceId;
        if (topLevelProductPriceIdCamel !== null && topLevelProductPriceIdCamel !== undefined && topLevelProductPriceIdCamel !== '') {
            return topLevelProductPriceIdCamel;
        }

        // Пріоритет 3: productPrice.price.productPriceId (camelCase - основне місце з API)
        const priceProductPriceId = item.productPrice?.price?.productPriceId;
        if (priceProductPriceId !== null && priceProductPriceId !== undefined && priceProductPriceId !== '') {
            return priceProductPriceId;
        }

        // Пріоритет 4: productPrice.priceOld.productPriceId (camelCase - якщо є знижка)
        const priceOldProductPriceId = item.productPrice?.priceOld?.productPriceId;
        if (priceOldProductPriceId !== null && priceOldProductPriceId !== undefined && priceOldProductPriceId !== '') {
            return priceOldProductPriceId;
        }

        // Пріоритет 5: productPrice.price.product_price_id (snake_case fallback)
        const snakeCasePriceId = item.productPrice?.price?.product_price_id;
        if (snakeCasePriceId !== null && snakeCasePriceId !== undefined && snakeCasePriceId !== '') {
            return snakeCasePriceId;
        }

        // Пріоритет 6: productPrice.price_old.product_price_id (snake_case fallback)
        const snakeCasePriceOldId = item.productPrice?.price_old?.product_price_id;
        if (snakeCasePriceOldId !== null && snakeCasePriceOldId !== undefined && snakeCasePriceOldId !== '') {
            return snakeCasePriceOldId;
        }

        // Пріоритет 6a: select_type.productPriceId (camelCase, для товарів з кошика)
        const selectTypeProductPriceIdCamel = item.select_type?.productPriceId;
        if (selectTypeProductPriceIdCamel !== null && selectTypeProductPriceIdCamel !== undefined && selectTypeProductPriceIdCamel !== '') {
            return selectTypeProductPriceIdCamel;
        }

        // Пріоритет 6b: select_type.product_price_id (snake_case, для товарів з кошика)
        const selectTypeProductPriceId = item.select_type?.product_price_id;
        if (selectTypeProductPriceId !== null && selectTypeProductPriceId !== undefined && selectTypeProductPriceId !== '') {
            return selectTypeProductPriceId;
        }

        // Пріоритет 7: product.product_price_id (для товарів з кошика)
        const productProductPriceId = item.product?.product_price_id;
        if (productProductPriceId !== null && productProductPriceId !== undefined && productProductPriceId !== '') {
            return productProductPriceId;
        }

        // Пріоритет 8: select_type.id (для товарів з кошика - це може бути product_price_id)
        const selectTypeId = item.select_type?.id;
        if (selectTypeId !== null && selectTypeId !== undefined && selectTypeId !== '') {
            return selectTypeId;
        }

        // Пріоритет 9: id на верхньому рівні (fallback)
        const topLevelId = item.id || item.item_id;
        if (topLevelId !== null && topLevelId !== undefined && topLevelId !== '') {
            return topLevelId;
        }

        // Якщо product_price_id не знайдено - повертаємо порожній рядок (порожня колонка)
        return '';
    }
    /**
     * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
     * @param {any} id - ID для нормалізації
     * @returns {number|string} Нормалізований ID
     */
    normalizeId(id) {
        if (id === null || id === undefined) return '';
        const num = Number(id);
        if (!Number.isNaN(num) && Number.isFinite(num)) return num;
        return ('' + id);
    }
    /**
     * Generate unique transaction ID
     * @param {string} prefix - Optional prefix
     * @returns {string} Unique transaction ID
     */
    generateTransactionId(prefix = '') {
        const timestamp = Date.now();
        const random = Math.random().toString(36).substr(2, 9);
        return `${prefix}${timestamp}_${random}`;
    }
    /**
     * Check if transaction was already processed
     * @param {string} transactionId - Transaction ID
     * @returns {boolean} True if already processed
     */
    isTransactionProcessed(transactionId) {
        return this.processedTransactions.has(transactionId);
    }
    /**
     * Get processed transactions count
     * @returns {number} Number of processed transactions
     */
    getProcessedCount() {
        return this.processedTransactions.size;
    }
    /**
     * Clear processed transactions (for testing)
     */
    clearProcessedTransactions() {
        this.processedTransactions.clear();
    }
    /**
     * Отримує поточну категорію з роуту
     * @returns {string}
     */
    getCurrentCategoryFromRoute() {
        try {
            // Перевіряємо, чи є Vue router доступний
            if (typeof window !== 'undefined' && window.$nuxt) {
                const route = window.$nuxt.$route;
                if (route && route.params && route.params.slug) {
                    return route.params.slug;
                }
            }
            // Альтернативний спосіб - через URL
            const path = window.location.pathname;
            const pathParts = path.split('/');
            const katalogIndex = pathParts.findIndex(part => part === 'katalog');
            if (katalogIndex !== -1 && pathParts[katalogIndex + 1]) {
                const slug = pathParts[katalogIndex + 1];
                return slug;
            }
            return '';
        } catch (e) {
            console.warn('Помилка отримання категорії з роуту:', e);
            return '';
        }
    }
    /**
     * Отримує назву категорії з каталогу за slug
     * @param {string} slug - Slug категорії
     * @returns {string}
     */
    getCategoryNameFromCatalog(slug) {
        if (!slug) {
            return '';
        }
        // Список каталогів з футера (правильні slug з бази даних)
        const catalogMap = {
            'monolitnii-polikarbonat': 'Монолітний полікарбонат',
            'profilyovanii-polikarbonat': 'Профільований полікарбонат',
            'sotovii-polikarbonat': 'Сотовий полікарбонат',
            'vstavka-do-pokrivli-iz-sendvich-paneli': 'Вставка до покрівлі із сендвіч панелі',
            'svitloprozori-fasadni-systemy': 'Світлопрозорі фасадні системи',
            'svitloprozori-pokrivelni-systemy': 'Світлопрозорі покрівельні системи',
            'aksessuary': 'Аксесуари',
            'rishennya-dlya-avtomobiliv': 'Рішення для автомобілів',
            'agroteplitsi': 'Агротеплиці'
        };
        const categoryName = catalogMap[slug];
        if (categoryName) {
            return categoryName;
        } else {
            return slug;
        }
    }
}
// Create global instance
if (typeof window !== 'undefined') {
    window.purchaseTracker = new PurchaseTracker();
}
export default PurchaseTracker;
